import { DropdownItem } from '../components/Dropdown/config';

/**
 * Which workflow statuses only a manager may move a job INTO, by job type:
 * Archived on every programme, and Complete on RWC and RWD. A job moved there
 * by mistake drops off the board's working columns (Archived drops off it
 * entirely), so it can be lost without anyone noticing.
 *
 * Mirrors workflow_status_is_manager_only() in
 * supabase/schemas/07_workflows/functions.sql. The database trigger is the
 * gate; this only disables the same options so a user is not refused after
 * the fact. Change both together.
 *
 * A stage takes its parent project's job type.
 */
export const isManagerOnlyWorkflowStatus = (
  jobType: string | null | undefined,
  statusName: string | null | undefined,
): boolean => {
  if (statusName === 'Archived') return true;
  if (statusName === 'Complete') return jobType === 'RWC' || jobType === 'RWD';
  return false;
};

/** Said wherever a non-manager is stopped: the board's notice and the trigger's refusal. */
export const managerOnlyStatusMessage = (statusName: string): string =>
  `Only a manager can move a job to ${statusName}.`;

/** What a locked option says, so the reason is in the list and not a mystery. */
export const MANAGER_ONLY_LABEL = 'Managers only';

/** The same, appended to a dropdown option's text. */
export const MANAGER_ONLY_SUFFIX = ` (${MANAGER_ONLY_LABEL.toLowerCase()})`;

/**
 * Whether this viewer's status controls offer the manager-only statuses.
 * Unlocked while the role is still loading, as the board does: the trigger is
 * the gate, and locking a manager out for that window is the worse failure.
 */
export const canUseManagerOnlyStatuses = (role: { isManager: boolean; loading: boolean }): boolean =>
  role.isManager || role.loading;

/**
 * A workflow status dropdown's items, with the manager-only statuses disabled
 * unless `canUseManagerOnly` (see canUseManagerOnlyStatuses).
 *
 * The status the row is SAVED in stays enabled and unlabelled: a stage form
 * resends its status on every save, picking it again changes nothing, and the
 * closed dropdown shows the selected item's label, which must read as the plain
 * status. Pass the saved workflow_status_id, never a displayed fallback (a
 * stage with no status shows its project's): the trigger compares against the
 * saved value, so exempting a fallback offers a move it then refuses.
 */
export const workflowStatusDropdownItems = (
  statuses: { id: number; status_name: string }[],
  jobType: string | null | undefined,
  canUseManagerOnly: boolean,
  savedStatusId: string | number | null | undefined,
): DropdownItem[] =>
  statuses.map(ws => {
    const locked = !canUseManagerOnly
      && String(ws.id) !== String(savedStatusId ?? '')
      && isManagerOnlyWorkflowStatus(jobType, ws.status_name);
    return {
      id: String(ws.id),
      label: locked ? `${ws.status_name}${MANAGER_ONLY_SUFFIX}` : ws.status_name,
      value: String(ws.id),
      disabled: locked,
    };
  });
